import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { getDistance, getFitZoom, getPinchedZoom, getZoomAnchorScrollDelta } from '../zoom'

interface PinchStart {
    distance: number
    zoom: number
}

interface ZoomAnchor {
    x: number
    y: number
    ratio: number
}

interface UsePinchZoomReturnValues {
    zoom: number
    contentWidth: number
    contentHeight: number
}

/**
 * Pinch to zoom on the content inside containerRef only, instead of the whole page. Returns the zoom to scale the content with,
 * and the unscaled size of the content. By default the content is zoomed to fit the container, but never above its own size.
 */
export const usePinchZoom = (
    containerRef: RefObject<HTMLDivElement>,
    contentRef: RefObject<HTMLDivElement>,
    maxZoom: number,
): UsePinchZoomReturnValues => {
    const [fitZoom, setFitZoom] = useState<number>(1)
    const [contentWidth, setContentWidth] = useState<number>(0)
    const [contentHeight, setContentHeight] = useState<number>(0)
    const [pinchedZoom, setPinchedZoom] = useState<number>()
    const zoom = Math.max(pinchedZoom || 0, fitZoom)

    const zoomRef = useRef<number>(zoom)
    const pointers = useRef<Map<number, PointerEvent>>(new Map())
    const pinchStart = useRef<PinchStart>()
    const anchor = useRef<ZoomAnchor>()

    useEffect(() => {
        const container = containerRef.current
        const content = contentRef.current

        if (! container || ! content) {
            return undefined
        }

        // A transform doesn't affect layout, so the content's own size is unaffected by the zoom.
        const getContainerFitZoom = (): number => getFitZoom(container.clientWidth, content.offsetWidth)

        const resizeObserver = new ResizeObserver(() => {
            setFitZoom(getContainerFitZoom())
            setContentWidth(content.offsetWidth)
            setContentHeight(content.offsetHeight)
        })

        const handlePointerDown = (event: PointerEvent): void => {
            pointers.current.set(event.pointerId, event)

            if (pointers.current.size === 2) {
                const [firstPointer, secondPointer] = [...pointers.current.values()]
                pinchStart.current = { distance: getDistance(firstPointer, secondPointer), zoom: zoomRef.current }
            }
        }

        const handlePointerMove = (event: PointerEvent): void => {
            if (! pointers.current.has(event.pointerId)) {
                return
            }

            pointers.current.set(event.pointerId, event)

            if (pointers.current.size !== 2 || ! pinchStart.current) {
                return
            }

            const [firstPointer, secondPointer] = [...pointers.current.values()]
            const newZoom = getPinchedZoom(
                pinchStart.current.zoom,
                pinchStart.current.distance,
                getDistance(firstPointer, secondPointer),
                getContainerFitZoom(),
                maxZoom,
            )
            const { left, top } = container.getBoundingClientRect()

            // Keep the point between both fingers in place, see useLayoutEffect below.
            anchor.current = {
                x: (firstPointer.clientX + secondPointer.clientX) / 2 - left,
                y: (firstPointer.clientY + secondPointer.clientY) / 2 - top,
                ratio: newZoom / zoomRef.current,
            }

            setPinchedZoom(newZoom)
        }

        const handlePointerEnd = (event: PointerEvent) => {
            pointers.current.delete(event.pointerId)

            if (pointers.current.size < 2) {
                pinchStart.current = undefined
            }
        }

        // Safari still zooms the whole page on pinch, unless its proprietary gesture events are canceled.
        const handleGestureStart = (event: Event) => {
            event.preventDefault()
        }

        resizeObserver.observe(container)
        resizeObserver.observe(content)
        container.addEventListener('pointerdown', handlePointerDown)
        container.addEventListener('pointermove', handlePointerMove)
        container.addEventListener('pointerup', handlePointerEnd)
        container.addEventListener('pointercancel', handlePointerEnd)
        container.addEventListener('gesturestart', handleGestureStart, { passive: false })

        return () => {
            resizeObserver.disconnect()
            container.removeEventListener('pointerdown', handlePointerDown)
            container.removeEventListener('pointermove', handlePointerMove)
            container.removeEventListener('pointerup', handlePointerEnd)
            container.removeEventListener('pointercancel', handlePointerEnd)
            container.removeEventListener('gesturestart', handleGestureStart)
        }
    }, [containerRef, contentRef, maxZoom])

    useLayoutEffect(() => {
        const container = containerRef.current
        zoomRef.current = zoom

        if (! container || ! anchor.current) {
            return
        }

        const { x, y, ratio } = anchor.current
        anchor.current = undefined

        container.scrollLeft += getZoomAnchorScrollDelta(container.scrollLeft + x, ratio)
        window.scrollBy(0, getZoomAnchorScrollDelta(y, ratio))
    }, [zoom])

    return { zoom, contentWidth, contentHeight }
}
